import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Star, Loader2, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { reviewsApi } from '../services/api';
import { useAuth } from '../AuthContext';
import { useSEO } from '../hooks/useSEO';
import reviewImage from '../assets/images/reviews.webp';

const ReviewsPage = () => {
  const { isAuthenticated } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useSEO({
    title: 'Guest Reviews | OlivOak Villa',
    description: 'Read guest reviews and testimonials about OlivOak Villa, Harare. See what our guests say about their luxury accommodation experience.'
  });

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch reviews and stats in parallel
      const [reviewsData, statsData] = await Promise.all([
        reviewsApi.getAll(),
        reviewsApi.getStats()
      ]);

      // Transform API data to match frontend format
      const transformedReviews = (reviewsData || []).map(review => ({
        id: review.id,
        guestName: review.guest_name,
        country: review.country,
        rating: review.rating,
        reviewText: review.review_text,
        date: review.created_at,
        verified: review.verified
      }));

      setReviews(transformedReviews);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
      setError('Unable to load reviews at this time. Please try again later.');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const averageRating = stats
    ? stats.average_rating.toFixed(1)
    : reviews.length > 0 
      ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)
      : '0.0';

  const totalReviews = stats ? stats.total_reviews : reviews.length;
  const ratingDistribution = stats?.rating_distribution || null;

  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin" size={40} style={{ color: 'var(--olive-primary)' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }} data-testid="reviews-page">
      {/* Hero Banner */}
      <section
        className="relative py-24"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${reviewImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container text-center">
          <h1 className="hero-large mb-4" style={{ color: '#ffffff' }}>
            Guest Reviews
          </h1>
          <p className="body-large" style={{ color: '#f0f0f0' }}>
            See what our guests are saying
          </p>
        </div>
      </section>

      {/* Rating Summary */}
      <section className="py-16" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="container max-w-4xl text-center">
          <div
            className="inline-block p-12"
            style={{
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-light)'
            }}
          >
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={32}
                  fill={i < Math.round(parseFloat(averageRating)) ? 'var(--text-primary)' : 'none'}
                  style={{ color: 'var(--text-primary)' }}
                />
              ))}
            </div>
            <p className="hero-medium mb-2" data-testid="average-rating">{averageRating} / 5.0</p>
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              Based on {totalReviews} verified {totalReviews === 1 ? 'review' : 'reviews'}
            </p>

            {/* Rating Distribution */}
            {ratingDistribution && totalReviews > 0 && (
              <div className="mt-8 max-w-xs mx-auto space-y-2">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = ratingDistribution[String(star)] || 0;
                  const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                  return (
                    <div key={star} className="flex items-center gap-3">
                      <span className="body-small w-4 text-right" style={{ color: 'var(--text-secondary)' }}>
                        {star}
                      </span>
                      <Star size={14} fill="var(--text-primary)" style={{ color: 'var(--text-primary)' }} />
                      <div className="flex-1 h-2" style={{ backgroundColor: 'var(--border-light)' }}>
                        <div
                          className="h-full"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: 'var(--olive-primary)',
                            transition: 'width 0.5s ease'
                          }}
                        />
                      </div>
                      <span className="body-small w-8" style={{ color: 'var(--text-light)' }}>
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Reviews List */}
      <section className="section-padding">
        <div className="container max-w-4xl">
          {error && (
            <div className="mb-8 p-4 text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                {error}
              </p>
            </div>
          )}

          {/* CTA for logged-in users */}
          {isAuthenticated && (
            <div
              className="mb-8 p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
              style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
            >
              <div>
                <p className="heading-3 mb-1">Stayed with us?</p>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  Share your experience — head to your dashboard to write a review for a completed booking.
                </p>
              </div>
              <Link to="/dashboard?action=review">
                <Button className="btn-primary flex items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
                  <MessageSquare size={16} />
                  Write a Review
                </Button>
              </Link>
            </div>
          )}

          {!error && reviews.length === 0 ? (
            <div
              className="p-12 text-center"
              style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
            >
              <Star size={48} className="mx-auto mb-4" style={{ color: 'var(--border-light)' }} />
              <p className="heading-3 mb-2">No Reviews Yet</p>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Be the first to share your experience at OlivOak Villa.
              </p>
              {!isAuthenticated && (
                <Link to="/login">
                  <Button className="btn-primary mt-6">
                    Sign In to Leave a Review
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-8" data-testid="reviews-list">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="p-8"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-light)'
                  }}
                  data-testid={`review-item-${review.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="heading-3 mb-1">{review.guestName}</h3>
                      {review.country && (
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {review.country}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star
                            key={i}
                            size={16}
                            fill="var(--text-primary)"
                            style={{ color: 'var(--text-primary)' }}
                          />
                        ))}
                      </div>
                      {review.verified && (
                        <span
                          className="text-xs px-2 py-1"
                          style={{
                            backgroundColor: 'var(--status-success)',
                            color: 'var(--bg-primary)'
                          }}
                        >
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <p className="body-regular mb-4" style={{ color: 'var(--text-primary)', lineHeight: '1.8' }}>
                    {review.reviewText}
                  </p>
                  
                  <p className="text-meta" style={{ color: 'var(--text-light)' }}>
                    {new Date(review.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;
